// İSG uzmanı portalı — İş İzni Tipleri (Ayarlar; api-contract §22, tenant-scoped). Firma kendi
// iş izni tiplerini tanımlar (ad, tehlike sınıfı, istenen belgeler) — izinler (§14) bu tiplerden
// birini seçer. Liste (UTable sözleşmesi: page/pageSize/search + total/filteredTotal) + aktif
// filtresi, oluştur/düzenle (PermitTypeFormModal — istenen belge çoklu seçici), sil (soft, onay).
// Auth gate gerçek Value = `permit-types` (PermitTypesGate).

import {
  Alert,
  Button,
  Card,
  CardContent,
  ConfirmDialog,
  EmptyState,
  HazardTag,
  Pagination,
  type PermitTypeDto,
  RowActionsMenu,
  SelectMenu,
  type SelectOption,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableMessageRow,
  TableRow,
  TableSkeletonRows,
  TableToolbar,
  TableToolbarFilter,
  Tag,
  permitTypesApi,
  resolveApiErrorMessage,
} from '@isgportal/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ClipboardList, Plus, SearchX } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ShellHeaderAction } from '../../components/layout/ShellHeaderAction';
import { PermitTypeFormModal } from '../../components/permit-types/PermitTypeFormModal';
import { permitTypesKeys } from '../../lib/permitTypesKeys';
import { useTableDensity } from '../../lib/uiPrefs';
import { useDebouncedValue } from '../../lib/useDebouncedValue';

const PAGE_SIZE = 20;

/** Aktif filtresi string değeri → `isActive` param (undefined = hepsi). */
function activeParam(value: string): boolean | undefined {
  if (value === 'active') {
    return true;
  }
  if (value === 'inactive') {
    return false;
  }
  return undefined;
}

export function PermitTypesListPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search);
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);
  const [message, setMessage] = useState<string | null>(null);

  const [createOpen, setCreateOpen] = useState(false);
  const [editType, setEditType] = useState<PermitTypeDto | null>(null);
  const [deleteType, setDeleteType] = useState<PermitTypeDto | null>(null);

  function resetPage<T>(setter: (v: T) => void) {
    return (value: T) => {
      setter(value);
      setPage(1);
    };
  }

  const statusOptions: SelectOption[] = useMemo(
    () => [
      { value: '', label: t('permitTypes.list.filter.allStatuses') },
      { value: 'active', label: t('permitTypes.list.filter.active') },
      { value: 'inactive', label: t('permitTypes.list.filter.inactive') },
    ],
    [t],
  );

  const params = {
    isActive: activeParam(statusFilter),
    search: debouncedSearch,
    page,
    pageSize: PAGE_SIZE,
  };

  const query = useQuery({
    queryKey: permitTypesKeys.listParams(params),
    queryFn: () => permitTypesApi.listPermitTypes(params),
    placeholderData: (prev) => prev,
  });

  const deleteMutation = useMutation({
    mutationFn: (uuid: string) => permitTypesApi.deletePermitType(uuid),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: permitTypesKeys.list() });
      queryClient.invalidateQueries({ queryKey: permitTypesKeys.options() });
      setMessage(t('permitTypes.list.deleted'));
      setDeleteType(null);
    },
  });

  const rows = query.data?.items ?? [];
  const [density, setDensity] = useTableDensity();
  const hasActiveFilters = Boolean(debouncedSearch || statusFilter);
  function clearFilters() {
    setSearch('');
    setStatusFilter('');
    setPage(1);
  }

  return (
    <div className="flex flex-col gap-5">
      <ShellHeaderAction>
        <Button
          onClick={() => {
            setMessage(null);
            setCreateOpen(true);
          }}
        >
          <Plus className="h-4 w-4" aria-hidden="true" />
          {t('permitTypes.list.new')}
        </Button>
      </ShellHeaderAction>

      {message ? <Alert tone="success">{message}</Alert> : null}

      <Card className="carbon:p-0">
        <CardContent className="flex flex-col gap-3 carbon:gap-0 carbon:p-0">
          <TableToolbar
            summary={
              query.data
                ? t('permitTypes.list.count', { count: query.data.filteredTotal })
                : undefined
            }
            search={search}
            onSearchChange={resetPage(setSearch)}
            searchLabel={t('permitTypes.list.search')}
            searchPlaceholder={t('permitTypes.list.searchPlaceholder')}
            density={density}
            onDensityChange={setDensity}
            densityLabel={t('common.compactRows')}
            filters={
              <TableToolbarFilter>
                <SelectMenu
                  ariaLabel={t('permitTypes.list.filter.status')}
                  placeholder={t('permitTypes.list.filter.allStatuses')}
                  options={statusOptions}
                  value={statusFilter}
                  onValueChange={resetPage(setStatusFilter)}
                />
              </TableToolbarFilter>
            }
          />

          <Table caption={t('permitTypes.title')} density={density}>
            <TableHeader>
              <TableRow>
                <TableHead>{t('permitTypes.list.col.name')}</TableHead>
                <TableHead>{t('permitTypes.list.col.hazard')}</TableHead>
                <TableHead className="text-right">
                  {t('permitTypes.list.col.requiredDocs')}
                </TableHead>
                <TableHead>{t('permitTypes.list.col.status')}</TableHead>
                <TableHead className="w-12">
                  <span className="sr-only">{t('permitTypes.common.actions')}</span>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody aria-busy={query.isPending || undefined}>
              {query.isPending ? (
                <TableSkeletonRows columns={5} />
              ) : query.isError ? (
                <TableMessageRow colSpan={5}>
                  {resolveApiErrorMessage(query.error, t)}
                </TableMessageRow>
              ) : rows.length === 0 ? (
                <TableMessageRow colSpan={5}>
                  {hasActiveFilters ? (
                    <EmptyState
                      icon={SearchX}
                      title={t('permitTypes.list.noResults')}
                      description={t('common.noResults.body')}
                      action={
                        <Button variant="ghost" size="sm" onClick={clearFilters}>
                          {t('common.clearFilters')}
                        </Button>
                      }
                      role="status"
                    />
                  ) : (
                    <EmptyState
                      icon={ClipboardList}
                      title={t('permitTypes.list.empty')}
                      description={t('permitTypes.list.empty.body')}
                      action={
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setMessage(null);
                            setCreateOpen(true);
                          }}
                        >
                          <Plus className="h-4 w-4" aria-hidden="true" />
                          {t('permitTypes.list.new')}
                        </Button>
                      }
                    />
                  )}
                </TableMessageRow>
              ) : (
                rows.map((type) => (
                  <TableRow key={type.uuid}>
                    <TableCell>
                      {/* Birincil "aç" = ad hücresi (S33 Slice 4 QA fix, defekt #4) — ayrı bir
                          görünüm rotası yok, "açmak" düzenleme modalını açmakla AYNI. */}
                      <button
                        type="button"
                        onClick={() => {
                          setMessage(null);
                          setEditType(type);
                        }}
                        className="block text-left font-medium text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                      >
                        {type.name}
                      </button>
                      {type.description ? (
                        <span className="block text-xs text-muted">{type.description}</span>
                      ) : null}
                    </TableCell>
                    <TableCell className="text-muted">
                      {/* Somut sınıf → outline hazard Tag; `null` → "Diğer" metin (§22; izin
                          açılışında elle sorulur — §14.1). Hazard'ın kendisi bir DURUM değil, o
                          yüzden "Diğer" bir Tag DEĞİL, düz metin kalır. */}
                      {type.hazardClass ? (
                        <HazardTag hazardClass={type.hazardClass} />
                      ) : (
                        t('permitTypes.form.hazardNone')
                      )}
                    </TableCell>
                    <TableCell className="text-right text-muted tnum">
                      {type.requiredDocumentCount}
                    </TableCell>
                    <TableCell>
                      <Tag color={type.isActive ? 'green' : 'gray'}>
                        {type.isActive
                          ? t('permitTypes.status.active')
                          : t('permitTypes.status.inactive')}
                      </Tag>
                    </TableCell>
                    <TableCell className="w-12 px-2">
                      <RowActionsMenu
                        ariaLabel={t('permitTypes.list.action.openMenu', { name: type.name })}
                        items={[
                          {
                            key: 'delete',
                            label: t('permitTypes.list.action.delete'),
                            destructive: true,
                            onSelect: () => {
                              setMessage(null);
                              setDeleteType(type);
                            },
                          },
                        ]}
                      />
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>

          {query.data ? (
            <Pagination
              page={page}
              pageSize={PAGE_SIZE}
              filteredTotal={query.data.filteredTotal}
              onPageChange={setPage}
              labels={{
                nav: t('permitTypes.pagination.nav'),
                previous: t('permitTypes.pagination.previous'),
                next: t('permitTypes.pagination.next'),
              }}
            />
          ) : null}
        </CardContent>
      </Card>

      <PermitTypeFormModal
        open={createOpen}
        onOpenChange={setCreateOpen}
        onSuccess={(msg) => setMessage(msg)}
      />
      <PermitTypeFormModal
        open={editType !== null}
        onOpenChange={(open) => !open && setEditType(null)}
        permitType={editType}
        onSuccess={(msg) => setMessage(msg)}
      />
      <ConfirmDialog
        open={deleteType !== null}
        onOpenChange={(open) => !open && setDeleteType(null)}
        title={t('permitTypes.delete.title')}
        body={t('permitTypes.delete.body', { name: deleteType?.name ?? '' })}
        confirmLabel={t('permitTypes.delete.confirm')}
        loading={deleteMutation.isPending}
        error={deleteMutation.isError ? deleteMutation.error : undefined}
        onConfirm={() => deleteType && deleteMutation.mutate(deleteType.uuid)}
      />
    </div>
  );
}
